import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import getAPI from "../../service/getAPI";
import { format } from "date-fns";
import { debounce } from "lodash";
import { generatePdfFromElement, copyElementToClipboardAsImage } from "../../utils/pdfUtils";
import { Modal } from "react-bootstrap";

function POS() {
  const { master } = getAPI();

  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cart, setCart] = useState([]);
  const [freeOffers, setFreeOffers] = useState([]); // 🟢 FREE PRODUCT OFFERS

  // Helper to synchronously get initial store profile from localStorage
  const getInitialStoreProfile = () => {
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          store_id: parsed.store_id || localStorage.getItem("store_id") || "",
          store_name: parsed.store_name || parsed.user_name || parsed.username || parsed.name || "",
          mobile_no: parsed.mobile_no || parsed.phone || parsed.contact || parsed.store_phone || "",
          address: parsed.address || "",
          area: parsed.area || "",
          city: parsed.city || "",
          state: parsed.state || "",
          country: parsed.country || "",
          pincode: parsed.pincode || "",
        };
      }
    } catch (e) {
      console.error(e);
    }
    return {
      store_id: localStorage.getItem("store_id") || "",
      store_name: "",
      mobile_no: "",
      address: "",
      area: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
    };
  };

  // Store / Branch Profile state (Loaded dynamically from user profile & API)
  const [storeProfile, setStoreProfile] = useState(getInitialStoreProfile);
  const [storeName, setStoreName] = useState(() => getInitialStoreProfile().store_name);
  const [storePhone, setStorePhone] = useState(() => getInitialStoreProfile().mobile_no);
  const [storeId, setStoreId] = useState(() => getInitialStoreProfile().store_id || 1);

  const fetchActiveBranchProfile = async (activeStoreId) => {
    if (!activeStoreId) return;
    try {
      const res = await axios.post(master.storeSelect, { store_id: Number(activeStoreId) });
      if (res.data.flag === 1 && Array.isArray(res.data.data) && res.data.data.length > 0) {
        const found = res.data.data[0];
        const profile = {
          store_id: found.store_id || activeStoreId,
          store_name: found.store_name || "",
          mobile_no: found.mobile_no ? String(found.mobile_no) : "",
          address: found.address || "",
          area: found.area || "",
          city: found.city || "",
          state: found.state || "",
          country: found.country || "",
          pincode: found.pincode ? String(found.pincode) : "",
        };
        setStoreProfile(profile);
        if (profile.store_name) setStoreName(profile.store_name);
        if (profile.mobile_no) setStorePhone(profile.mobile_no);

        const stored = localStorage.getItem("user");
        if (stored) {
          try {
            const u = JSON.parse(stored);
            localStorage.setItem("user", JSON.stringify({ ...u, ...profile }));
          } catch (e) {
            console.error(e);
          }
        }
      }
    } catch (err) {
      console.error("Failed to fetch branch profile:", err);
    }
  };

  useEffect(() => {
    try {
      const init = getInitialStoreProfile();
      if (init.store_id) {
        setStoreId(init.store_id);
        fetchActiveBranchProfile(init.store_id);
      }
      if (init.store_name) setStoreName(init.store_name);
      if (init.mobile_no) setStorePhone(init.mobile_no);
      setStoreProfile(init);
    } catch (e) {
      console.error(e);
    }
  }, []);

  // Category and Search State
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [productSearch, setProductSearch] = useState("");
  
  // View Mode: "grid" or "list" (Persisted in localStorage)
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem("pos_view_mode") || "grid";
  });

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem("pos_view_mode", mode);
  };

  const categories = [
    { label: "All Products", value: "All", icon: "bi-grid-fill" },
    { label: "Nirant", value: "Nirant", icon: "bi-flower1" },
    { label: "B12 Food", value: "B12", icon: "bi-capsule" },
    { label: "100 gm", value: "100", icon: "bi-tag" },
    { label: "200 gm", value: "200", icon: "bi-tag" },
    { label: "400 gm", value: "400", icon: "bi-tag" },
  ];

  // Global Discount State
  const [globalDiscountType, setGlobalDiscountType] = useState("none"); // "none", "percent", "flat"
  const [globalDiscountValue, setGlobalDiscountValue] = useState(0);
  const [globalDiscountBase, setGlobalDiscountBase] = useState("sellingPrice"); // "mrp", "sellingPrice"
  const [isGlobalDiscountManual, setIsGlobalDiscountManual] = useState(false);

  // Discount Modal State
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [editingDiscountItem, setEditingDiscountItem] = useState(null); // null = global, object = specific item
  const [tempDiscountType, setTempDiscountType] = useState("none");
  const [tempDiscountValue, setTempDiscountValue] = useState("");
  const [tempDiscountBase, setTempDiscountBase] = useState("mrp");
  
  // --- Gift Eligibility State ---
  const [giftEligibility, setGiftEligibility] = useState(null); // null | "checking" | "eligible" | "not_eligible" | "error"
  const [giftCheckResult, setGiftCheckResult] = useState(null);

  const checkGiftEligibility = async (mobile) => {
    if (!mobile) return;
    const cleanMobile = String(mobile).trim().replace(/\D/g, "");
    if (cleanMobile.length < 10) return;

    setGiftEligibility("checking");
    try {
      // Reusing your existing checkNewCustomer endpoint to determine if they have prior orders
      const res = await axios.get(`${master.checkNewCustomer}?mobile=${cleanMobile}`);
      const details = res.data?.customerDetails || res.data?.data || {};
      
      // If orders exist, they are repeat (not eligible for new customer gift)
      const isRepeat = res.data?.flag === 1 && (details.ordersExist !== false);
      
      setGiftCheckResult(details);
      setGiftEligibility(isRepeat ? "not_eligible" : "eligible");
    } catch (err) {
      console.error("Gift check failed:", err);
      if (err.response?.status === 404) {
        // 404 usually implies a completely new customer in this context
        setGiftCheckResult({});
        setGiftEligibility("eligible");
      } else {
        setGiftEligibility("error");
      }
    }
  };

  const resetGiftEligibility = () => {
    setGiftEligibility(null);
    setGiftCheckResult(null);
  };

  // Customer selection
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentType, setPaymentType] = useState("cash");

  // Bill generation
  const [showBill, setShowBill] = useState(false);
  const [orderResponse, setOrderResponse] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const billRef = useRef(null);

  // New Customer Modal
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState("");
  const [newCustomerMobile, setNewCustomerMobile] = useState("");
  const [isAddingCustomer, setIsAddingCustomer] = useState(false);

  // Optional Phone on Checkout Modal
  const [showOptionalPhoneModal, setShowOptionalPhoneModal] = useState(false);
  const [optionalMobile, setOptionalMobile] = useState("");
  const [optionalName, setOptionalName] = useState("");
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Brand-wide customer type (New / Repeat — any Bilipatra order, not branch-specific)
  const [customerBrandType, setCustomerBrandType] = useState(null); // null | "checking" | "new" | "repeat" | "error"
  const [modalBrandType, setModalBrandType] = useState(null);
  const [customerOrderHistory, setCustomerOrderHistory] = useState([]);
  const brandCheckRef = useRef(0);

  const checkBrandCustomerType = async (mobile, { onResult, autofillName = false, isLocalBranchCustomer = false } = {}) => {
    const cleanMobile = String(mobile || "").trim().replace(/\D/g, "");
    if (cleanMobile.length < 10) {
      onResult?.(null);
      return;
    }

    const checkId = ++brandCheckRef.current;
    onResult?.("checking");

    try {
      // Pass store_id just in case your backend supports it now or in the future
      const res = await axios.get(`${master.checkNewCustomer}?mobile=${cleanMobile}&store_id=${storeId}`);
      if (checkId !== brandCheckRef.current) return;

      const details = res.data?.customerDetails || res.data?.data || {};
      
      // Store orders history if present
      if (details.orders && Array.isArray(details.orders)) {
        setCustomerOrderHistory(details.orders);
      } else {
        setCustomerOrderHistory([]);
      }
      
      // Check if the API says they have orders AT ALL
      const hasAnyOrders = res.data?.flag === 1 || details.ordersExist === true;
      
      // Check if API explicitly returns a branch flag (fallback)
      const apiSaysBranchRepeat = details.branchOrdersExist === true || details.branch_orders_exist === true;

      let customerStatus = "new";

      if (hasAnyOrders) {
        // 🚨 PRIORITY 1: Branch Repeat 
        // Triggered if the API explicitly says so, OR if the frontend knows we pulled them from the local branch search
        if (apiSaysBranchRepeat || isLocalBranchCustomer) {
          customerStatus = "branch_repeat";
        } 
        // 🚨 PRIORITY 2: Company Repeat
        // Triggered if they have orders, but aren't tied to this local branch
        else {
          customerStatus = "company_repeat";
        }
      }

      if (autofillName) {
        const fetchedName = details.name || details.customer_name || details.customerName;
        if (fetchedName) {
          if (typeof autofillName === "function") {
            autofillName(fetchedName);
          } else {
            setNewCustomerName((current) => (current.trim() ? current : fetchedName));
          }
        }
      }

      onResult?.(customerStatus);
    } catch (err) {
      console.error("Brand customer check failed:", err);
      if (checkId !== brandCheckRef.current) return;
      onResult?.(err.response?.status === 404 ? "new" : "error");
    }
  };

  const resetBrandCustomerType = () => {
    brandCheckRef.current += 1;
    setCustomerBrandType(null);
    setModalBrandType(null);
    setCustomerOrderHistory([]);
  };

  const renderBrandCustomerTag = (type, count = 0, onClick = null, compact = false) => {
    if (!type) return null;

    const styles = {
      checking: { bg: "#f1f5f9", color: "#475569", border: "#e2e8f0", label: "Checking..." },
      new: { bg: "#dcfce7", color: "#166534", border: "#bbf7d0", label: "Unique / New" },
      branch_repeat: { bg: "#e0e7ff", color: "#3730a3", border: "#c7d2fe", label: "Branch Repeat" },
      company_repeat: { bg: "#fef3c7", color: "#92400e", border: "#fde68a", label: "Company Repeat" },
      error: { bg: "#fee2e2", color: "#991b1b", border: "#fecaca", label: "Unknown" },
      // Fallback just in case your API still just returns 'repeat'
      repeat: { bg: "#fef3c7", color: "#92400e", border: "#fde68a", label: "Company Repeat" } 
    };
    const style = styles[type] || styles.error;

    return (
      <span
        className={`badge d-inline-flex align-items-center gap-1 ${onClick ? 'cursorP' : ''}`}
        style={{
          fontSize: compact ? "9px" : "10px",
          backgroundColor: style.bg,
          color: style.color,
          border: `1px solid ${style.border}`,
          fontWeight: "700",
          letterSpacing: "0.02em",
          cursor: onClick ? 'pointer' : 'default'
        }}
        onClick={onClick}
      >
        {type === "checking" && (
          <span className="spinner-border spinner-border-sm" style={{ width: "10px", height: "10px" }} />
        )}
        {style.label}
        {count > 0 && (type === 'branch_repeat' || type === 'company_repeat' || type === 'repeat') && (
            <span className="badge bg-danger text-white ms-1 rounded-pill shadow-sm" style={{ fontSize: "9px", padding: "0.2em 0.5em" }}>
              {count}
            </span>
        )}
        {onClick && (type === 'branch_repeat' || type === 'company_repeat' || type === 'repeat') && (
            <i className="bi bi-box-arrow-up-right ms-1"></i>
        )}
      </span>
    );
  };

  const convertNumberToWords = (num) => {
    const a = [
      "", "One ", "Two ", "Three ", "Four ", "Five ", "Six ", "Seven ", "Eight ", "Nine ", "Ten ", "Eleven ", "Twelve ", "Thirteen ", "Fourteen ", "Fifteen ", "Sixteen ", "Seventeen ", "Eighteen ", "Nineteen "
    ];
    const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    const inWords = (n) => {
      if ((n = n.toString()).length > 9) return "overflow";
      let n_array = ("000000000" + n).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
      if (!n_array) return "";
      let str = "";
      str += n_array[1] != 0 ? (a[Number(n_array[1])] || b[n_array[1][0]] + " " + a[n_array[1][1]]) + "Crore " : "";
      str += n_array[2] != 0 ? (a[Number(n_array[2])] || b[n_array[2][0]] + " " + a[n_array[2][1]]) + "Lakh " : "";
      str += n_array[3] != 0 ? (a[Number(n_array[3])] || b[n_array[3][0]] + " " + a[n_array[3][1]]) + "Thousand " : "";
      str += n_array[4] != 0 ? (a[Number(n_array[4])] || b[n_array[4][0]] + " " + a[n_array[4][1]]) + "Hundred " : "";
      str += n_array[5] != 0 ? (str != "" ? "and " : "") + (a[Number(n_array[5])] || b[n_array[5][0]] + " " + a[n_array[5][1]]) : "";
      return str.trim();
    };

    const integerPart = Math.floor(Number(num) || 0);
    const words = inWords(integerPart);
    return words ? `${words} Rupees Only` : "Zero Rupees Only";
  };

  useEffect(() => {
    loadProducts();
    
    // 🟢 Load free product offers on startup
    axios.post(master.freeOfferGetAll, { branch_id: 0 })
      .then((res) => { 
        if (res.data.flag === 1) {
          // Only sync active offers
          const activeOffers = res.data.data.filter(offer => offer.is_active === 1);
          setFreeOffers(activeOffers);
        }
      })
      .catch((err) => console.error('Error loading free offers:', err));
    
    // 🟢 Load global configurations
    axios.post(master.configGetAll)
      .then((res) => {
        if (res.data.flag === 1) {
          const configs = res.data.data;
          const autoDiscountConfig = configs.find(c => c.configuration_key === 'enable_auto_discount');
          const baseConfig = configs.find(c => c.configuration_key === 'default_discount_base');
          
          if (autoDiscountConfig) {
            setIsGlobalDiscountManual(autoDiscountConfig.configuration_value !== '1' && autoDiscountConfig.configuration_value !== 'true');
          }
          if (baseConfig) {
            setGlobalDiscountBase(baseConfig.configuration_value);
          }
        }
      })
      .catch(err => console.error('Error loading config:', err));
  }, []);

  // Debounced API search for customers
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadCustomers(customerSearch);
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [customerSearch]);

  // Auto-check brand customer type when a customer with mobile is selected
  useEffect(() => {
    if (!selectedCustomer?.mobile_no) {
      setCustomerBrandType(null);
      setCustomerOrderHistory([]);
      return;
    }
    checkBrandCustomerType(selectedCustomer.mobile_no, { onResult: setCustomerBrandType });
  }, [selectedCustomer?.customer_id, selectedCustomer?.mobile_no]);

  // Auto-check in Add Customer modal as mobile is typed
  useEffect(() => {
    if (!showAddCustomer || newCustomerMobile.length < 10) {
      setModalBrandType(null);
      return;
    }
    
    const delayDebounceFn = setTimeout(() => {
      checkBrandCustomerType(newCustomerMobile, {
        onResult: setModalBrandType,
        autofillName: true,
      });
    }, 400); // Waits 400ms after you stop typing to make the API call
    
    return () => clearTimeout(delayDebounceFn);
  }, [showAddCustomer, newCustomerMobile]);

  // Auto-check in Checkout Optional Phone modal as mobile is typed
  useEffect(() => {
    // Only check if the modal is open and we have at least 10 digits
    if (!showOptionalPhoneModal || optionalMobile.length < 10) {
      if (showOptionalPhoneModal) setModalBrandType(null);
      return;
    }
    
    const delayDebounceFn = setTimeout(() => {
      checkBrandCustomerType(optionalMobile, {
        onResult: setModalBrandType,
        // Autofill the name directly into the optionalName state
        autofillName: (name) => setOptionalName((curr) => curr.trim() ? curr : name),
      });
    }, 400); // 400ms debounce
    
    return () => clearTimeout(delayDebounceFn);
  }, [showOptionalPhoneModal, optionalMobile]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const res = await axios.post(master.productSelectAll, {
        pageIndex: 1,
        pageSize: 1000,
        searchParam: { global_search: "", is_admin: false, store_id: storeId },
      });
      if (res.data.flag === 1) {
        setProducts(res.data.data.result || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async (searchTerm = "") => {
    try {
      const res = await axios.post(master.customerListPagination, {
        page: 1,
        limit: 20,
        global_search: searchTerm,
        store_id: storeId,
      });
      if (res.data.flag === 1) {
        setCustomers(res.data.data.result || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // --- Free Product Auto-Sync ---
  useEffect(() => {
    if (freeOffers.length === 0 || products.length === 0) return;

    setCart((prevCart) => {
      // Count non-free items per product_id
      const buyCounts = {};
      prevCart.forEach((item) => {
        if (!item.is_free_item) {
          buyCounts[item.product_id] = (buyCounts[item.product_id] || 0) + item.qty;
        }
      });

      // Calculate earned free items
      const earned = {}; // product_id -> qty
      freeOffers.forEach((offer) => {
        const bought = buyCounts[offer.buy_product_id] || 0;
        const bundles = Math.floor(bought / offer.buy_qty);
        if (bundles > 0) {
          earned[offer.free_product_id] = (earned[offer.free_product_id] || 0) + bundles * offer.free_qty;
        }
      });

      // Remove all existing free items from cart
      let newCart = prevCart.filter((item) => !item.is_free_item);

      // Add earned free items back
      Object.entries(earned).forEach(([productId, freeQty]) => {
        const pid = Number(productId);
        const productInfo = products.find((p) => p.product_id === pid);
        if (!productInfo) return;
        newCart = [
          ...newCart,
          {
            ...productInfo,
            product_id: pid,
            qty: freeQty,
            mrp: 0,
            sellingPrice: 0,
            totalMrp: 0,
            totalSellingPrice: 0,
            price: 0,
            is_free_item: true,
            hasCustomDiscount: false,
            discountType: 'none',
            discountValue: 0,
            discountBase: globalDiscountBase,
          },
        ];
      });

      return newCart;
    });
  }, [cart.filter(i => !i.is_free_item).map(i => `${i.product_id}:${i.qty}`).join(','), freeOffers, products, globalDiscountBase]);

  // --- Cart Engine Logic ---
  const isEligibleForAutoDiscount = (item) => {
    return !item.is_free_item; // 🟢 Free items are NOT eligible for auto discount
  };

  useEffect(() => {
    if (isGlobalDiscountManual) return;

    let eligibleQty = 0;
    cart.forEach((item) => {
      if (isEligibleForAutoDiscount(item)) {
        eligibleQty += item.qty;
      }
    });

    let percent = 0;
    if (eligibleQty >= 6) percent = 25;
    else if (eligibleQty >= 3) percent = 20;
    else if (eligibleQty >= 2) percent = 15;
    else if (eligibleQty >= 1) percent = 10;

    if (percent > 0) {
      setGlobalDiscountType("percent");
      setGlobalDiscountValue(percent);
    } else {
      setGlobalDiscountType("none");
      setGlobalDiscountValue(0);
    }
  }, [cart, isGlobalDiscountManual]);

  const getItemCalculatedDiscount = (item) => {
    if (item.hasCustomDiscount) {
      const baseAmount = item.discountBase === "mrp" ? item.totalMrp : item.totalSellingPrice;
      if (item.discountType === "flat") return item.discountValue;
      if (item.discountType === "percent") return baseAmount * (item.discountValue / 100);
      return 0;
    }

    if (globalDiscountValue > 0) {
      if (!isGlobalDiscountManual && !isEligibleForAutoDiscount(item)) return 0;

      const globalBaseAmount = globalDiscountBase === "mrp" ? item.totalMrp : item.totalSellingPrice;

      if (globalDiscountType === "percent") {
        return globalBaseAmount * (globalDiscountValue / 100);
      } else if (globalDiscountType === "flat") {
        let cartEligibleTotal = 0;
        cart.forEach((c) => {
          if (c.hasCustomDiscount) return;
          if (!isGlobalDiscountManual && !isEligibleForAutoDiscount(c)) return;
          cartEligibleTotal += globalDiscountBase === "mrp" ? c.totalMrp : c.totalSellingPrice;
        });
        if (cartEligibleTotal > 0) {
          return globalDiscountValue * (globalBaseAmount / cartEligibleTotal);
        }
      }
    }
    return 0;
  };

  const getDiscountDetails = (item) => {
    if (item.hasCustomDiscount) {
      return item.discountType === "percent" ? `(${item.discountValue}%)` : `(₹${item.discountValue})`;
    } else if (globalDiscountValue > 0) {
      if (!isGlobalDiscountManual && !isEligibleForAutoDiscount(item)) return "";
      const suffix = isGlobalDiscountManual ? "Global" : "Qty";
      return globalDiscountType === "percent"
        ? `(${globalDiscountValue}% ${suffix})`
        : `(₹${globalDiscountValue} ${suffix})`;
    }
    return "";
  };

  const cartSubtotal = cart.reduce((acc, item) => {
    const base = globalDiscountBase === "mrp" ? item.totalMrp : item.totalSellingPrice;
    return acc + (item.hasCustomDiscount ? (item.discountBase === "mrp" ? item.totalMrp : item.totalSellingPrice) : base);
  }, 0);

  const cartTotalDiscount = cart.reduce((acc, item) => acc + getItemCalculatedDiscount(item), 0);
  const cartFinalTotal = Math.max(0, cartSubtotal - cartTotalDiscount);
  const totalCartItemCount = cart.reduce((acc, item) => acc + item.qty, 0);

  const addToCart = (product) => {
    const existing = cart.find((item) => item.product_id === product.product_id && !item.is_free_item);
    const mrp = Number(product.product_price || 0);
    const hasBranchPrice = product.branch_price !== null && product.branch_price !== undefined;
    const branchPrice = hasBranchPrice ? Number(product.branch_price) : mrp;
    const discountPrice = Number(product.product_discount_price || 0);
    const sellingPrice = hasBranchPrice ? branchPrice : discountPrice > 0 ? discountPrice : branchPrice;

    if (existing) {
      setCart(
        cart.map((item) =>
          item.product_id === product.product_id
            ? {
                ...item,
                qty: item.qty + 1,
                totalMrp: (item.qty + 1) * mrp,
                totalSellingPrice: (item.qty + 1) * sellingPrice,
              }
            : item
        )
      );
    } else {
      setCart([
        ...cart,
        {
          ...product,
          qty: 1,
          mrp,
          sellingPrice,
          totalMrp: mrp,
          totalSellingPrice: sellingPrice,
          hasCustomDiscount: false,
          discountType: "none",
          discountValue: 0,
          discountBase: "sellingPrice",
        },
      ]);
    }
  };

  const updateQty = (productId, delta) => {
    setCart((prev) =>
      prev
        .map((item) => {
          if (item.product_id === productId && !item.is_free_item) {
            const newQty = item.qty + delta;
            if (newQty <= 0) return null;
            return {
              ...item,
              qty: newQty,
              totalMrp: newQty * item.mrp,
              totalSellingPrice: newQty * item.sellingPrice,
            };
          }
          return item;
        })
        .filter(Boolean)
    );
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter((item) => !(item.product_id === productId && !item.is_free_item)));
  };

  const clearCart = () => {
    if (cart.length > 0 && window.confirm("Are you sure you want to clear the current order?")) {
      setCart([]);
    }
  };

  // --- Discount Handlers ---
  const openDiscountModal = (item = null) => {
    setEditingDiscountItem(item);
    if (item) {
      setTempDiscountType(item.discountType === "none" ? "percent" : item.discountType);
      setTempDiscountValue(item.discountValue > 0 ? item.discountValue.toString() : "");
      setTempDiscountBase(item.discountBase);
    } else {
      setTempDiscountType(globalDiscountType === "none" ? "percent" : globalDiscountType);
      setTempDiscountValue(globalDiscountValue > 0 ? globalDiscountValue.toString() : "");
      setTempDiscountBase(globalDiscountBase);
    }
    setShowDiscountModal(true);
  };

  const applyDiscountModal = () => {
    const val = parseFloat(tempDiscountValue) || 0;

    if (editingDiscountItem) {
      setCart(
        cart.map((item) => {
          if (item.product_id === editingDiscountItem.product_id) {
            return {
              ...item,
              hasCustomDiscount: val > 0,
              discountType: val > 0 ? tempDiscountType : "none",
              discountValue: val,
              discountBase: tempDiscountBase,
            };
          }
          return item;
        })
      );
    } else {
      if (isGlobalDiscountManual) {
        setGlobalDiscountType(val > 0 ? tempDiscountType : "none");
        setGlobalDiscountValue(val);
      }
      setGlobalDiscountBase(tempDiscountBase);
    }
    setShowDiscountModal(false);
  };

  const removeDiscountModal = () => {
    if (editingDiscountItem) {
      setCart(
        cart.map((item) => {
          if (item.product_id === editingDiscountItem.product_id) {
            return { ...item, hasCustomDiscount: false, discountType: "none", discountValue: 0 };
          }
          return item;
        })
      );
    } else {
      setIsGlobalDiscountManual(true);
      setGlobalDiscountType("none");
      setGlobalDiscountValue(0);
    }
    setShowDiscountModal(false);
  };

  const toggleAutoDiscount = () => {
    const turningManualOn = !isGlobalDiscountManual;
    if (turningManualOn) {
      setIsGlobalDiscountManual(true);
      setGlobalDiscountType("none");
      setGlobalDiscountValue(0);
    } else {
      setIsGlobalDiscountManual(false);
    }
  };

  // --- Checkout ---
  const handleAddCustomer = async (e) => {
    e.preventDefault();
    if (!newCustomerName || !newCustomerMobile) return;
    setIsAddingCustomer(true);
    try {
      const res = await axios.post(master.customerInsert, {
        customer_name: newCustomerName,
        mobile_no: Number(newCustomerMobile),
        address: "Walk-in",
      });
      if (res.data.flag === 1) {
        const newCustomer = {
          customer_id: res.data.data.customer_id,
          customer_name: res.data.data.customer_name,
          mobile_no: res.data.data.mobile_no,
        };
        setCustomers([newCustomer, ...customers]);
        setSelectedCustomer(newCustomer);
        setShowAddCustomer(false);
        setNewCustomerName("");
        setNewCustomerMobile("");
      } else {
        alert(res.data.message || "Failed to add customer");
      }
    } catch (err) {
      console.error(err);
      alert("Error adding customer.");
    } finally {
      setIsAddingCustomer(false);
    }
  };

 const handleSelectWalkIn = () => {
    setSelectedCustomer({
      customer_id: 0,
      customer_name: "Walk-in Customer",
      mobile_no: "",
      address: "Walk-in",
    });
    resetBrandCustomerType();
    setCustomerSearch(""); // Moved inside the function
  }; // Only one closing brace

  const handleInitiateBookOrder = () => {
    if (cart.length === 0) {
      alert("Cart is empty. Please add products to order.");
      return;
    }

    // If customer is empty or has no mobile number, prompt optionally for phone number
    if (!selectedCustomer || !selectedCustomer.mobile_no || selectedCustomer.customer_id === 0) {
      setOptionalMobile(selectedCustomer?.mobile_no ? String(selectedCustomer.mobile_no) : "");
      setOptionalName(selectedCustomer?.customer_name && selectedCustomer.customer_name !== "Walk-in Customer" ? selectedCustomer.customer_name : "");
      setShowOptionalPhoneModal(true);
      return;
    }

    // Customer already registered with phone number -> book immediately
    executeOrderBooking(selectedCustomer);
  };

  const handleSkipPhoneAndBook = () => {
    setShowOptionalPhoneModal(false);
    const walkInCust = selectedCustomer || {
      customer_id: 0,
      customer_name: "Walk-in Customer",
      mobile_no: "",
      address: "Walk-in",
    };
    executeOrderBooking(walkInCust);
  };

  const handleSavePhoneAndBook = async (e) => {
    if (e) e.preventDefault();
    const cleanMobile = optionalMobile.trim().replace(/\D/g, "");

    if (cleanMobile.length > 0) {
      const custName = optionalName.trim() || `Customer (${cleanMobile})`;
      try {
        const res = await axios.post(master.customerInsert, {
          customer_name: custName,
          mobile_no: Number(cleanMobile),
          address: "Walk-in",
        });

        let targetCustomer = null;
        if (res.data.flag === 1) {
          targetCustomer = {
            customer_id: res.data.data.customer_id,
            customer_name: res.data.data.customer_name,
            mobile_no: res.data.data.mobile_no,
          };
          setCustomers((prev) => [targetCustomer, ...prev]);
        } else {
          // If customer already exists, find or construct
          const existing = customers.find((c) => String(c.mobile_no).includes(cleanMobile));
          targetCustomer = existing || {
            customer_id: 0,
            customer_name: custName,
            mobile_no: cleanMobile,
            address: "Walk-in",
          };
        }

        setSelectedCustomer(targetCustomer);
        setShowOptionalPhoneModal(false);
        executeOrderBooking(targetCustomer);
      } catch (err) {
        console.error(err);
        const fallbackCust = {
          customer_id: 0,
          customer_name: custName,
          mobile_no: cleanMobile,
          address: "Walk-in",
        };
        setShowOptionalPhoneModal(false);
        executeOrderBooking(fallbackCust);
      }
    } else {
      // User left mobile empty and hit save -> proceed as Walk-in
      handleSkipPhoneAndBook();
    }
  };

  const executeOrderBooking = async (customerToUse) => {
    if (cart.length === 0) {
      alert("Cart is empty. Please add products to order.");
      return;
    }

    setIsSubmitting(true);
    try {
      const apiGlobalDiscountPercent = globalDiscountType === "percent" ? globalDiscountValue : 0;

      const payload = {
        store_id: storeId,
        customer_id: customerToUse.customer_id,
        order_type: paymentType,
        discount_percent: Number(apiGlobalDiscountPercent.toFixed(4)),
        product_list: cart.map((item) => {
          let itemDiscountPercent = 0;
          if (item.hasCustomDiscount && item.discountValue > 0) {
            if (item.discountType === "percent") {
              itemDiscountPercent = item.discountValue;
            } else if (item.discountType === "flat") {
              const base = item.discountBase === "mrp" ? item.totalMrp : item.totalSellingPrice;
              itemDiscountPercent = base > 0 ? (item.discountValue / base) * 100 : 0;
            }
          }

          let itemBasePrice = item.sellingPrice;
          if (item.hasCustomDiscount) {
            itemBasePrice = item.discountBase === "mrp" ? item.mrp : item.sellingPrice;
          } else {
            itemBasePrice = globalDiscountBase === "mrp" ? item.mrp : item.sellingPrice;
          }

          return {
            product_id: item.product_id,
            qty: item.qty,
            price: itemBasePrice,
            discount: Number(itemDiscountPercent.toFixed(4)),
          };
        }),
        total_amount: cartFinalTotal,
        paid_amount: paymentType === "credit" ? 0 : cartFinalTotal,
        order_status: paymentType === "credit" ? "credit" : "success",
      };

      const res = await axios.post(master.createBranchOrder, payload);

      if (res.data.flag === 1) {
        setOrderResponse({
          ...res.data.data,
          customer: customerToUse,
          cart: cart.map((item) => ({ ...item, _billDiscount: getItemCalculatedDiscount(item) })),
          totalAmount: cartFinalTotal,
          subtotal: cartSubtotal,
          totalDiscount: cartTotalDiscount,
          paymentType: paymentType,
          discountBase: globalDiscountBase,
          storeProfile: storeProfile,
          branchName: storeProfile.store_name || storeName || "",
          storePhone: storeProfile.mobile_no || storePhone || "",
        });

        // Clear cart and customer after successful booking
        setCart([]);
        setSelectedCustomer(null);
        setCustomerSearch("");
        setShowBill(true);
      } else {
        alert(res.data.message || "Failed to create order");
      }
    } catch (err) {
      console.error(err);
      alert("Error booking order.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const buildWhatsAppBillText = () => {
    if (!orderResponse) return "";
    const customerName = orderResponse.customer?.customer_name || "Grahak";
    const profile = orderResponse.storeProfile || storeProfile;
    const branch = profile.store_name || orderResponse.branchName || "Retail Counter";
    const phone = profile.mobile_no || orderResponse.storePhone || "";
    const fullAddress = [profile.address, profile.area, profile.city, profile.state, profile.pincode].filter(Boolean).join(", ");
    const dateStr = format(new Date(), "dd/MM/yyyy");

    let text = `*નમસ્તે ${customerName},*\n\n`;
    text += `આપની ખરીદી બદલ હૃદયપૂર્વક આભાર.\n`;
    text += `Order નંબર #${orderResponse.order_id} માટેનો ઇન્વોઇસ PDF સ્વરૂપે અહીં જોડેલ છે — કૃપા કરીને તપાસ કરો.\n\n`;
    if (phone) {
      text += `જો કોઈ પ્રશ્ન કે સહાયની જરૂર હોય, તો નિઃસંકોચ અમારો સંપર્ક કરો (+91 ${phone}).\n\n`;
    }
    text += `*BILIPATRA*\n`;
    text += `--------------------------------\n`;
    text += `${branch}\n`;
    if (fullAddress) {
      text += `*સરનામું (Address):* ${fullAddress}\n`;
    }
    if (phone) {
      text += `*સ્ટોર સંપર્ક (Store Phone):* +91 ${phone}\n`;
    }
    text += `*બિલ નંબર (Bill No):* #${orderResponse.order_id}\n`;
    text += `*તારીખ (Date):* ${dateStr}\n`;
    text += `--------------------------------\n`;
    text += `*ઓર્ડર વિગત (Items Ordered):*\n`;

    orderResponse.cart.forEach((item, index) => {
      const useMrp = orderResponse.discountBase === "mrp";
      const unitPrice = Number(useMrp ? item.mrp : item.sellingPrice || item.price || 0);
      const gross = unitPrice * item.qty;
      const disc = Number(item._billDiscount || 0);
      const finalAmt = Math.max(0, gross - disc);
      const weight = item.product_weight ? ` (${item.product_weight})` : "";

      text += `${index + 1}. *${item.product_name}${weight}*\n`;
      text += `   Qty: ${item.qty} × ₹${unitPrice.toFixed(2)}`;
      if (disc > 0) {
        text += ` (-₹${disc.toFixed(2)}) = *₹${finalAmt.toFixed(2)}*\n`;
      } else {
        text += ` = *₹${finalAmt.toFixed(2)}*\n`;
      }
    });

    text += `--------------------------------\n`;
    if (orderResponse.totalDiscount > 0) {
      text += `કુલ રકમ (Sub Total): ₹${orderResponse.subtotal.toFixed(2)}\n`;
      text += `ડિસ્કાઉન્ટ (Discount): -₹${orderResponse.totalDiscount.toFixed(2)}\n`;
    }
    text += `*કુલ ચૂકવવાપાત્ર રકમ (Grand Total): ₹${orderResponse.totalAmount.toFixed(2)}*\n`;
    const paymentStatus =
      orderResponse.paymentType === "credit"
        ? "બાકી (Credit / Udhar)"
        : `ચૂકવેલ (PAID - ${String(orderResponse.paymentType || "CASH").toUpperCase()})`;
    text += `*ચૂકવણી (Payment):* ${paymentStatus}\n`;
    text += `--------------------------------\n`;
    text += `_બિલીપત્રા પસંદ કરવા બદલ આપનો ખૂબ ખૂબ આભાર!_`;

    return text;
  };

  const [defaultWaMethod, setDefaultWaMethod] = useState(
    localStorage.getItem("preferredWaMethod") || "api"
  );

  const handleSendToCustomerWhatsApp = async (method = defaultWaMethod) => {
    if (!orderResponse) return;
    
    // Save as new default
    localStorage.setItem("preferredWaMethod", method);
    setDefaultWaMethod(method);

    const text = buildWhatsAppBillText();
    const rawPhone = String(orderResponse.customer?.mobile_no || "").trim().replace(/\D/g, "");
    let targetPhone = "";
    if (rawPhone.length === 10) {
      targetPhone = `91${rawPhone}`;
    } else if (rawPhone.length > 10) {
      targetPhone = rawPhone.startsWith("91") ? rawPhone : `91${rawPhone.slice(-10)}`;
    }

    const encodedText = encodeURIComponent(text);
    let waUrl = "";

    if (method === 'share') {
      try {
        if (navigator.canShare && billRef.current) {
          const element = billRef.current;
          const file = await generatePdfFromElement(element, `Bill_ORD-${orderResponse.order_id || "receipt"}.pdf`, "file");

          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: `Bill_ORD-${orderResponse.order_id || "receipt"}`,
              text: text
            });
            return;
          }
        }
      } catch (err) {
        console.error("Native share failed:", err);
      }
      alert("Your browser/OS does not support native PDF sharing.");
      return;
    } else if (method === 'app') {
      waUrl = targetPhone ? `whatsapp://send?phone=${targetPhone}&text=${encodedText}` : `whatsapp://send?text=${encodedText}`;
      window.location.href = waUrl;
      return;
    } else if (method === 'web') {
      waUrl = targetPhone ? `https://web.whatsapp.com/send?phone=${targetPhone}&text=${encodedText}` : `https://web.whatsapp.com/send?text=${encodedText}`;
      window.open(waUrl, "whatsapp_web_window");
      return;
    } else {
      waUrl = targetPhone
        ? `https://wa.me/${targetPhone}?text=${encodedText}`
        : `https://wa.me/?text=${encodedText}`;
      window.open(waUrl, "whatsapp_api_window");
      return;
    }
  };

  const handleCopyMessage = async () => {
    if (!orderResponse) return;
    const text = buildWhatsAppBillText();
    try {
      await navigator.clipboard.writeText(text);
      alert("Invoice message copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy text: ", err);
      alert("Failed to copy message. Please try manually.");
    }
  };

  const handlePrintBill = () => {
    if (!billRef.current) return;
    const printContent = billRef.current.innerHTML;
    const printWindow = window.open("", "_blank", "width=850,height=900");
    if (!printWindow) {
      window.print();
      return;
    }
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Retail Bill #${orderResponse?.order_id || ""}</title>
          <style>
            body { margin: 0; padding: 15px; font-family: 'Segoe UI', Arial, sans-serif; color: #0f172a; background: #fff; }
            @media print { body { margin: 0; padding: 5px; } @page { size: auto; margin: 8mm; } }
          </style>
        </head>
        <body>
          <div style="max-width: 650px; margin: 0 auto;">
            ${printContent}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 400);
  };

  const [defaultDocMethod, setDefaultDocMethod] = useState(
    localStorage.getItem("preferredDocMethod") || "print"
  );

  const handleDocumentAction = (method = defaultDocMethod) => {
    localStorage.setItem("preferredDocMethod", method);
    setDefaultDocMethod(method);
    if (method === 'pdf') {
      handleDownloadPDF();
    } else if (method === 'copy_img') {
      handleCopyImage();
    } else {
      handlePrintBill();
    }
  };

  const handleCopyImage = async () => {
    if (!billRef.current) return;
    setDownloadingPdf(true); // Re-using state for loading spinner
    const success = await copyElementToClipboardAsImage(billRef.current);
    setDownloadingPdf(false);
    if (success) {
      alert("Receipt copied to clipboard as an image! You can now paste it.");
    } else {
      alert("Failed to copy receipt to clipboard.");
    }
  };

  const handleDownloadPDF = async () => {
    if (!billRef.current) return;
    try {
      setDownloadingPdf(true);
      const element = billRef.current;
      await generatePdfFromElement(element, `Bill_ORD-${orderResponse?.order_id || "receipt"}.pdf`, "download");
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Failed to generate PDF. You can use 'Print Bill' to save as PDF.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleToggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch((err) => console.error(err));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch((err) => console.error(err));
    }
  };

  const isPopout = new URLSearchParams(window.location.search).get("popout") === "true";

  const handlePopoutWindow = () => {
    const width = 1440;
    const height = 900;
    const left = Math.max(0, (window.screen.width - width) / 2);
    const top = Math.max(0, (window.screen.height - height) / 2);
    const popoutUrl = `${window.location.origin}/admin/pos?popout=true`;
    window.open(
      popoutUrl,
      "BILIPATRA_POS_COUNTER",
      `width=${width},height=${height},top=${top},left=${left},status=no,menubar=no,toolbar=no,location=no,resizable=yes,scrollbars=yes`
    );
  };

  // Filter products by category and search
  const filteredProducts = products.filter((p) => {
    let matchesCat = true;
    if (selectedCategory !== "All") {
      const catLower = selectedCategory.toLowerCase();
      if (["100", "200", "400"].includes(catLower)) {
        matchesCat = (p.product_weight || "").toLowerCase().includes(catLower);
      } else {
        matchesCat = (p.product_name || "").toLowerCase().includes(catLower);
      }
    }

    let matchesSearch = true;
    if (productSearch.trim()) {
      const query = productSearch.toLowerCase().trim();
      matchesSearch =
        (p.product_name || "").toLowerCase().includes(query) ||
        (p.product_weight || "").toLowerCase().includes(query);
    }

    return matchesCat && matchesSearch;
  });

  const getProductCartQty = (productId) => {
    const item = cart.find((c) => c.product_id === productId && !c.is_free_item);
    return item ? item.qty : 0;
  };

  const renderTaxInvoiceModal = () => {
    if (!showBill || !orderResponse) return null;

    let grossSubTotal = 0;
    let totalDiscountsApplied = 0;
    orderResponse.cart.forEach((item) => {
      const useMrp = orderResponse.discountBase === "mrp";
      const unitPrice = Number(useMrp ? item.mrp : item.sellingPrice || item.price || 0);
      const gross = unitPrice * item.qty;
      const disc = Number(item._billDiscount || 0);
      grossSubTotal += gross;
      totalDiscountsApplied += disc;
    });

    const netTaxableAmount = Math.max(0, grossSubTotal - totalDiscountsApplied);
    const sgst = (netTaxableAmount * 0.025).toFixed(2);
    const cgst = (netTaxableAmount * 0.025).toFixed(2);

    let storedUser = {};
    try {
      storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    } catch (e) {}

    const activeProfile = orderResponse?.storeProfile || storeProfile || storedUser;

    const displayedBranchName =
      activeProfile?.store_name ||
      orderResponse?.branchName ||
      storeName ||
      storedUser.store_name ||
      storedUser.user_name ||
      storedUser.name ||
      "";

    const displayedBranchPhone =
      activeProfile?.mobile_no ||
      orderResponse?.storePhone ||
      storePhone ||
      storedUser.mobile_no ||
      storedUser.phone ||
      "";

    const rawAddress = activeProfile?.address || storedUser.address || "";
    const rawArea = activeProfile?.area || storedUser.area || "";
    const rawCity = activeProfile?.city || storedUser.city || "";
    const rawState = activeProfile?.state || storedUser.state || "";
    const rawPincode = activeProfile?.pincode || storedUser.pincode || "";

    const branchFullAddress = [rawAddress, rawArea, rawCity, rawState, rawPincode]
      .map((s) => (s ? String(s).trim() : ""))
      .filter(Boolean)
      .join(", ");

    return (
      <div className="modal show d-block" style={{ backgroundColor: "rgba(15, 23, 42, 0.75)", zIndex: 1060 }}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "14px", overflow: "hidden" }}>
            <div className="modal-header bg-success text-white py-3 px-4 d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-check-circle-fill fs-4"></i>
                <div>
                  <h5 className="modal-title fw-bold mb-0">Order Placed Successfully!</h5>
                  <small className="opacity-75">Bill #{orderResponse.order_id} • {orderResponse.customer?.customer_name || "Customer"}</small>
                </div>
              </div>
              <button type="button" className="btn-close btn-close-white" onClick={() => setShowBill(false)}></button>
            </div>

            <div className="modal-body p-4 bg-light" style={{ maxHeight: "75vh", overflowY: "auto" }}>
              <div
                ref={billRef}
                className="bg-white p-4 mx-auto rounded-3 shadow-sm"
                style={{
                  maxWidth: "600px",
                  color: "#0f172a",
                  fontFamily: "'Segoe UI', Arial, sans-serif",
                  border: "1px solid #e2e8f0",
                }}
              >
                {/* Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #198754", paddingBottom: "12px", marginBottom: "14px" }}>
                  {/* Left: Brand Name */}
                  <div style={{ maxWidth: "52%" }}>
                    <h3 style={{ margin: "0", color: "#198754", fontWeight: "800", letterSpacing: "0.5px" }}>BILIPATRA</h3>
                    <div style={{ fontWeight: "700", fontSize: "12px", color: "#1e293b", marginTop: "1px" }}>{displayedBranchName}</div>
                    {branchFullAddress && (
                      <div style={{ fontSize: "10px", color: "#64748b", marginTop: "2px", lineHeight: "1.3" }}>
                        {branchFullAddress}
                      </div>
                    )}
                    {displayedBranchPhone && (
                      <div style={{ fontSize: "10px", color: "#334155", marginTop: "1px" }}>
                        Store: <strong>+91 {displayedBranchPhone}</strong>
                      </div>
                    )}
                  </div>

                  {/* Right: Bill Type & Branch Name */}
                  <div style={{ textAlign: "right", maxWidth: "45%" }}>
                    <div style={{ display: "inline-block", backgroundColor: "#e8f5e9", color: "#1b5e20", fontWeight: "700", fontSize: "10px", padding: "2px 8px", borderRadius: "4px", border: "1px solid #c8e6c9", marginBottom: "3px" }}>
                      RETAIL BILL / CASH MEMO
                    </div>
                    {/* {displayedBranchName && (
                      <div style={{ fontSize: "12px", fontWeight: "800", color: "#0f172a" }}>
                        {displayedBranchName}{activeProfile?.store_id ? ` (Branch #${activeProfile.store_id})` : ""}
                      </div>
                    )} */}
                  </div>
                </div>

                {/* Customer & Invoice Info */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px", backgroundColor: "#f8fafc", padding: "10px 14px", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                  <div>
                    <div style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#64748b", letterSpacing: "0.5px" }}>Customer Details</div>
                    <div style={{ fontSize: "13px", fontWeight: "700", color: "#0f172a" }}>{orderResponse.customer?.customer_name || "Walk-in Customer"}</div>
                    <div style={{ fontSize: "11px", color: "#334155" }}>Phone: +91 {orderResponse.customer?.mobile_no || "N/A"}</div>
                    <div style={{ fontSize: "10px", color: "#64748b" }}>Address: {orderResponse.customer?.address || "Walk-in"}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#64748b", letterSpacing: "0.5px" }}>Invoice Info</div>
                    <div style={{ fontSize: "13px", fontWeight: "700", color: "#198754" }}>#{orderResponse.order_id}</div>
                    <div style={{ fontSize: "11px", color: "#334155" }}>Date: {format(new Date(), "dd/MM/yyyy hh:mm a")}</div>
                    <div style={{ fontSize: "10px", color: "#334155" }}>
                      Payment: <span style={{ fontWeight: "700", textTransform: "uppercase", color: orderResponse.paymentType === "credit" ? "#d97706" : "#16a34a" }}>{orderResponse.paymentType || "CASH"}</span>
                    </div>
                  </div>
                </div>

                {/* Items Table */}
                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "14px", fontSize: "11px" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#198754", color: "#ffffff", textAlign: "left" }}>
                      <th style={{ padding: "6px 8px", width: "25px", textAlign: "center" }}>#</th>
                      <th style={{ padding: "6px 8px" }}>Item</th>
                      <th style={{ padding: "6px 8px", width: "40px", textAlign: "center" }}>Qty</th>
                      <th style={{ padding: "6px 8px", width: "65px", textAlign: "right" }}>Rate</th>
                      <th style={{ padding: "6px 8px", width: "65px", textAlign: "right" }}>Disc</th>
                      <th style={{ padding: "6px 8px", width: "75px", textAlign: "right" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderResponse.cart.map((item, idx) => {
                      const useMrp = orderResponse.discountBase === "mrp";
                      const unitPrice = Number(useMrp ? item.mrp : item.sellingPrice || item.price || 0);
                      const gross = unitPrice * item.qty;
                      const disc = Number(item._billDiscount || 0);
                      const finalAmt = Math.max(0, gross - disc);
                      return (
                        <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "5px 8px", textAlign: "center", color: "#64748b" }}>{idx + 1}</td>
                          <td style={{ padding: "5px 8px" }}>
                            <div style={{ fontWeight: "600", color: "#0f172a" }}>{item.product_name}</div>
                            {item.product_weight && <div style={{ fontSize: "10px", color: "#64748b" }}>{item.product_weight}</div>}
                          </td>
                          <td style={{ padding: "5px 8px", textAlign: "center" }}>{item.qty}</td>
                          <td style={{ padding: "5px 8px", textAlign: "right" }}>₹{unitPrice.toFixed(2)}</td>
                          <td style={{ padding: "5px 8px", textAlign: "right", color: disc > 0 ? "#dc2626" : "#64748b" }}>
                            {disc > 0 ? `-₹${disc.toFixed(2)}` : "-"}
                          </td>
                          <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: "700" }}>₹{finalAmt.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Summary */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "14px" }}>
                  <div style={{ width: "230px", fontSize: "11px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                      <span style={{ color: "#64748b" }}>Sub Total:</span>
                      <span style={{ fontWeight: "600" }}>₹{grossSubTotal.toFixed(2)}</span>
                    </div>
                    {totalDiscountsApplied > 0 && (
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px", color: "#dc2626" }}>
                        <span>Discount:</span>
                        <span style={{ fontWeight: "600" }}>-₹{totalDiscountsApplied.toFixed(2)}</span>
                      </div>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between", borderTop: "2px solid #0f172a", paddingTop: "5px", marginTop: "4px", fontSize: "13px", fontWeight: "800", color: "#198754" }}>
                      <span>Grand Total:</span>
                      <span>₹{netTaxableAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "8px", fontSize: "10px", color: "#64748b", display: "flex", justifyContent: "space-between" }}>
                  <div>
                    <div>* Computer generated retail receipt.</div>
                    <div style={{ color: "#198754", fontWeight: "bold" }}>
                      Thank you for shopping at BILIPATRA!
                    </div>
                    {activeProfile.mobile_no && (
                      <div style={{ fontSize: "9px", color: "#64748b" }}>Helpline: +91 {activeProfile.mobile_no}</div>
                    )}
                  </div>
                  <div style={{ textAlign: "center", borderTop: "1px solid #94a3b8", width: "120px", paddingTop: "2px", fontWeight: "bold" }}>
                    Authorized Signatory
                    {activeProfile.store_name && (
                      <div style={{ fontSize: "8px", fontWeight: "normal", color: "#64748b" }}>{activeProfile.store_name}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer bg-white py-3 px-4 d-flex justify-content-between">
              <button type="button" className="btn btn-secondary px-3" onClick={() => setShowBill(false)}>
                <i className="bi bi-x-circle me-1"></i> Close
              </button>
              <div className="d-flex gap-2 flex-nowrap align-items-center">
                <div className="btn-group shadow-sm">
                  <button 
                    type="button" 
                    className="btn btn-outline-primary px-3 fw-bold" 
                    onClick={() => handleDocumentAction(defaultDocMethod)}
                    disabled={downloadingPdf}
                    title={defaultDocMethod === 'pdf' ? "Download PDF" : defaultDocMethod === 'copy_img' ? "Copy Bill Image" : "Print Bill"}
                  >
                    {defaultDocMethod === 'pdf' ? (
                      downloadingPdf ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-file-earmark-pdf me-1"></i>
                    ) : defaultDocMethod === 'copy_img' ? (
                      downloadingPdf ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-clipboard-data me-1"></i>
                    ) : (
                      <i className="bi bi-printer me-1"></i>
                    )}
                    {defaultDocMethod === 'pdf' ? "PDF" : defaultDocMethod === 'copy_img' ? "Copy Image" : "Print"}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-outline-primary dropdown-toggle dropdown-toggle-split" 
                    data-bs-toggle="dropdown" 
                    aria-expanded="false"
                    disabled={downloadingPdf}
                  >
                    <span className="visually-hidden">Toggle Dropdown</span>
                  </button>
              <ul className="dropdown-menu shadow border-0 mt-1">
                    <li>
                      <button className="dropdown-item py-2 d-flex justify-content-between align-items-center fw-medium" onClick={() => handleDocumentAction('print')}>
                        <span><i className="bi bi-printer me-2 text-primary fs-5"></i> Print Bill</span>
                        {defaultDocMethod === 'print' && <i className="bi bi-check-circle-fill text-success ms-3" title="Current Default"></i>}
                      </button>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <button className="dropdown-item py-2 d-flex justify-content-between align-items-center fw-medium" onClick={() => handleDocumentAction('pdf')}>
                        <span><i className="bi bi-file-earmark-pdf me-2 text-danger fs-5"></i> Download PDF</span>
                        {defaultDocMethod === 'pdf' && <i className="bi bi-check-circle-fill text-success ms-3" title="Current Default"></i>}
                      </button>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <button className="dropdown-item py-2 d-flex justify-content-between align-items-center fw-medium" onClick={() => handleDocumentAction('copy_img')}>
                        <span><i className="bi bi-clipboard-data me-2 text-secondary fs-5"></i> Copy Bill Image</span>
                        {defaultDocMethod === 'copy_img' && <i className="bi bi-check-circle-fill text-success ms-3" title="Current Default"></i>}
                      </button>
                    </li>
                  </ul>
                </div>
                <button type="button" className="btn btn-outline-secondary fw-bold px-2 shadow-sm" onClick={handleCopyMessage} title="Copy Message">
                  <i className="bi bi-clipboard me-1"></i> Copy
                </button>
                <div className="btn-group shadow-sm">
                  <button 
                    type="button" 
                    className="btn btn-success fw-bold px-3" 
                    onClick={() => handleSendToCustomerWhatsApp(defaultWaMethod)}
                    title="Send via your Default Method"
                  >
                    <i className="bi bi-whatsapp me-2"></i> Send ({orderResponse.customer?.mobile_no || "WhatsApp"})
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-success dropdown-toggle dropdown-toggle-split" 
                    data-bs-toggle="dropdown" 
                    aria-expanded="false"
                  >
                    <span className="visually-hidden">Toggle Dropdown</span>
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end shadow border-0 mt-1">
                    <li>
                      <button className="dropdown-item py-2 d-flex align-items-center fw-medium" onClick={() => handleSendToCustomerWhatsApp('share')}>
                        <i className="bi bi-file-earmark-pdf me-2 text-danger fs-5"></i> Share with PDF Attached
                        {defaultWaMethod === 'share' && <i className="bi bi-check-circle-fill text-success ms-auto ms-2" title="Current Default"></i>}
                      </button>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <button className="dropdown-item py-2 d-flex align-items-center fw-medium" onClick={() => handleSendToCustomerWhatsApp('web')}>
                        <i className="bi bi-browser-chrome me-2 text-muted"></i> Send via WhatsApp Web
                        {defaultWaMethod === 'web' && <i className="bi bi-check-circle-fill text-success ms-auto ms-2" title="Current Default"></i>}
                      </button>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <button className="dropdown-item py-2 d-flex align-items-center fw-medium" onClick={() => handleSendToCustomerWhatsApp('app')}>
                        <i className="bi bi-display me-2 text-muted fs-5"></i> Open Desktop App directly
                        <div className="ms-auto d-flex align-items-center gap-2">
                          <i className="bi bi-lightning-fill text-warning" title="Fastest Method"></i>
                          {defaultWaMethod === 'app' && <i className="bi bi-check-circle-fill text-success" title="Current Default"></i>}
                        </div>
                      </button>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <button className="dropdown-item py-2 d-flex align-items-center fw-medium" onClick={() => handleSendToCustomerWhatsApp('api')}>
                        <i className="bi bi-link-45deg me-2 text-muted fs-5"></i> Default Link (wa.me)
                        {defaultWaMethod === 'api' && <i className="bi bi-check-circle-fill text-success ms-auto ms-2" title="Current Default"></i>}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <main id="main" className={`main ${isPopout ? "p-3 m-0" : ""}`} style={isPopout ? { margin: 0, padding: "16px 20px" } : {}}>
      {/* Top Header Bar */}
      <div className="pagetitle d-flex justify-content-between align-items-center flex-wrap mb-3">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <h1 className="d-flex align-items-center gap-2 mb-0">
              <i className="bi bi-cart3 text-success"></i> Point of Sale
            </h1>

            {/* 🟢 Popout in Dedicated Window Button (Only if not already popped out) */}
            {!isPopout && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary rounded-circle shadow-sm p-0 d-flex align-items-center justify-content-center bg-white"
                style={{ width: "32px", height: "32px" }}
                onClick={handlePopoutWindow}
                title="Open in Dedicated Pop-out POS Window"
              >
                <i className="bi bi-box-arrow-up-right text-dark" style={{ fontSize: "13px" }}></i>
              </button>
            )}

            {/* 🟢 Fullscreen Toggle Button */}
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary rounded-circle shadow-sm p-0 d-flex align-items-center justify-content-center bg-white d-none d-sm-flex"
              style={{ width: "32px", height: "32px" }}
              onClick={handleToggleFullscreen}
              title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen Counter Mode"}
            >
              <i className={`bi ${isFullscreen ? "bi-fullscreen-exit" : "bi-arrows-fullscreen"} text-dark`} style={{ fontSize: "13px" }}></i>
            </button>

            {/* 🟢 Close Window Button (Only in Pop-out mode) */}
            {isPopout && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger shadow-sm px-2 py-0 fw-semibold"
                onClick={() => window.close()}
                title="Close Pop-out Counter"
              >
                <i className="bi bi-x-circle me-1"></i> Close Window
              </button>
            )}
          </div>

          {!isPopout && (
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><a href="/admin/dashboard">Home</a></li>
                <li className="breadcrumb-item active">POS Billing</li>
              </ol>
            </nav>
          )}
        </div>

        <div className="d-flex align-items-center gap-2">
          <span className="badge bg-success bg-opacity-10 text-success border border-success px-3 py-2 fw-semibold">
            <i className="bi bi-shop me-1"></i> {storeName ? `${storeName} Branch` : "Branch Counter"}
          </span>
          <span className="badge bg-light text-dark border px-3 py-2 fw-semibold d-none d-md-inline-block">
            <i className="bi bi-calendar-event me-1 text-primary"></i> {format(new Date(), "dd MMM yyyy")}
          </span>
        </div>
      </div>

      <div className="row g-3">
        {/* Left Column: Product Catalog Grid */}
        <div className="col-lg-8 col-xl-8">
          <div className="card shadow-sm border-0 d-flex flex-column" style={{ height: "calc(100vh - 145px)", minHeight: "600px", borderRadius: "12px" }}>
            {/* Search & Category Filter Toolbar */}
            <div className="card-header bg-white border-bottom p-3">
              <div className="row g-2 align-items-center mb-3">
                {/* Search Bar */}
                <div className="col-md-6 col-12">
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0 text-muted">
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 ps-0 bg-light"
                      placeholder="Search products by name or weight..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                    />
                    {productSearch && (
                      <button className="btn btn-light border border-start-0 text-muted" onClick={() => setProductSearch("")}>
                        <i className="bi bi-x-lg"></i>
                      </button>
                    )}
                  </div>
                </div>

                {/* View Mode Switcher & Product Counter */}
                <div className="col-md-6 col-12 d-flex justify-content-md-end align-items-center gap-3">
                  <span className="text-muted small d-none d-sm-inline">
                    Showing <strong className="text-dark">{filteredProducts.length}</strong> products
                  </span>

                  {/* 🟢 Grid | List Mode Switcher with saved preference */}
                  <div className="btn-group btn-group-sm shadow-sm" role="group" aria-label="View Mode">
                    <button
                      type="button"
                      className={`btn ${viewMode === "grid" ? "btn-success text-white fw-bold" : "btn-outline-secondary bg-white"}`}
                      onClick={() => handleViewModeChange("grid")}
                      title="Grid View"
                    >
                      <i className="bi bi-grid-fill me-1"></i> Grid
                    </button>
                    <button
                      type="button"
                      className={`btn ${viewMode === "list" ? "btn-success text-white fw-bold" : "btn-outline-secondary bg-white"}`}
                      onClick={() => handleViewModeChange("list")}
                      title="List View"
                    >
                      <i className="bi bi-list-ul me-1"></i> List
                    </button>
                  </div>
                </div>
              </div>

              {/* Category Filter Chips */}
              <div className="d-flex overflow-auto gap-2 pb-1" style={{ whiteSpace: "nowrap" }}>
                {categories.map((cat) => {
                  const isActive = selectedCategory === cat.value;
                  return (
                    <button
                      key={cat.value}
                      onClick={() => setSelectedCategory(cat.value)}
                      className={`btn btn-sm rounded-pill px-3 fw-bold d-flex align-items-center gap-1 transition-all ${
                        isActive
                          ? "btn-success text-white shadow-sm"
                          : "btn-light text-secondary border hover-bg-light"
                      }`}
                    >
                      <i className={`bi ${cat.icon}`}></i>
                      <span>{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Products Catalog Container (Grid / List View) */}
            <div className="card-body p-3 flex-grow-1 overflow-auto">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-success" role="status">
                    <span className="visually-hidden">Loading products...</span>
                  </div>
                  <div className="text-muted mt-2 small">Loading product catalog...</div>
                </div>
              ) : filteredProducts.length > 0 ? (
                viewMode === "grid" ? (
                  /* 🟢 GRID VIEW */
                  <div className="row row-cols-2 row-cols-sm-2 row-cols-md-3 row-cols-xl-3 g-3">
                    {filteredProducts.map((prod) => {
                      const mrp = Number(prod.product_price || 0);
                      const hasBranchPrice = prod.branch_price !== null && prod.branch_price !== undefined;
                      const branchPrice = hasBranchPrice ? Number(prod.branch_price) : mrp;
                      const discountPrice = Number(prod.product_discount_price || 0);
                      const sellingPrice = hasBranchPrice ? branchPrice : discountPrice > 0 ? discountPrice : branchPrice;
                      const inCartQty = getProductCartQty(prod.product_id);
                      const hasDiscount = sellingPrice < mrp;

                      return (
                        <div className="col" key={prod.product_id}>
                          <div
                            className={`card h-100 shadow-sm border position-relative transition-all ${
                              inCartQty > 0 ? "border-success bg-success bg-opacity-10" : "border-light-subtle bg-white"
                            }`}
                            style={{
                              borderRadius: "12px",
                              cursor: "pointer",
                              transition: "transform 0.15s ease, box-shadow 0.15s ease",
                            }}
                            onClick={() => addToCart(prod)}
                            onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-3px)")}
                            onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                          >
                            {/* In-Cart Floating Badge */}
                            {inCartQty > 0 && (
                              <span
                                className="position-absolute top-0 end-0 badge bg-success shadow-sm m-2 px-2 py-1"
                                style={{ zIndex: 2, fontSize: "11px", borderRadius: "8px" }}
                              >
                                <i className="bi bi-cart-check me-1"></i> {inCartQty} in cart
                              </span>
                            )}

                            <div className="card-body p-3 d-flex flex-column align-items-center text-center">
                              {/* Product Image */}
                              <div
                                className="rounded-3 d-flex align-items-center justify-content-center mb-2 overflow-hidden"
                                style={{
                                  width: "90px",
                                  height: "90px",
                                  backgroundColor: "#f8fafc",
                                  border: "1px solid #f1f5f9",
                                }}
                              >
                                {prod.product_image ? (
                                  <img
                                    src={prod.product_image}
                                    alt={prod.product_name}
                                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                  />
                                ) : (
                                  <i className="bi bi-box-seam text-secondary opacity-50 fs-1"></i>
                                )}
                              </div>

                              {/* Product Title */}
                              <h6 className="fw-bold text-dark mb-1 text-truncate w-100" title={prod.product_name} style={{ fontSize: "14px" }}>
                                {prod.product_name}
                              </h6>

                              {/* Weight Badge */}
                              {prod.product_weight && (
                                <span className="badge bg-light text-muted border mb-2" style={{ fontSize: "11px" }}>
                                  {prod.product_weight}
                                </span>
                              )}

                              {/* Pricing Tag */}
                              <div className="mt-auto d-flex align-items-center justify-content-center gap-2">
                                {hasDiscount ? (
                                  <>
                                    <span className="text-muted text-decoration-line-through small">₹{mrp}</span>
                                    <span className="text-success fw-bold fs-6">₹{sellingPrice}</span>
                                  </>
                                ) : (
                                  <span className="text-dark fw-bold fs-6">₹{sellingPrice}</span>
                                )}
                              </div>

                              {/* Quick Add / In-Cart Actions */}
                              {inCartQty > 0 ? (
                                <div
                                  className="d-flex align-items-center gap-1 w-100 mt-2"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-success text-white fw-semibold rounded-pill flex-grow-1"
                                    style={{ fontSize: "12px" }}
                                    onClick={() => addToCart(prod)}
                                  >
                                    <i className="bi bi-plus-lg me-1"></i> Add More
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-light text-danger rounded-circle border shadow-sm p-0 d-flex align-items-center justify-content-center"
                                    style={{ width: "30px", height: "30px", flexShrink: 0 }}
                                    onClick={() => removeFromCart(prod.product_id)}
                                    title="Remove from cart"
                                  >
                                    <i className="bi bi-trash3" style={{ fontSize: "13px" }}></i>
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success w-100 mt-2 fw-semibold rounded-pill"
                                  style={{ fontSize: "12px" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addToCart(prod);
                                  }}
                                >
                                  <i className="bi bi-plus-lg me-1"></i> Add to Order
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* 🟢 LIST VIEW */
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: "55px" }}>Item</th>
                          <th>Product Name</th>
                          <th style={{ width: "110px" }}>Weight</th>
                          <th className="text-end" style={{ width: "120px" }}>Price</th>
                          <th className="text-center" style={{ width: "160px" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProducts.map((prod) => {
                          const mrp = Number(prod.product_price || 0);
                          const hasBranchPrice = prod.branch_price !== null && prod.branch_price !== undefined;
                          const branchPrice = hasBranchPrice ? Number(prod.branch_price) : mrp;
                          const discountPrice = Number(prod.product_discount_price || 0);
                          const sellingPrice = hasBranchPrice ? branchPrice : discountPrice > 0 ? discountPrice : branchPrice;
                          const inCartQty = getProductCartQty(prod.product_id);
                          const hasDiscount = sellingPrice < mrp;

                          return (
                            <tr
                              key={prod.product_id}
                              className={inCartQty > 0 ? "table-success bg-opacity-25" : ""}
                            >
                              <td>
                                <div
                                  className="rounded-2 d-flex align-items-center justify-content-center overflow-hidden bg-light border"
                                  style={{ width: "42px", height: "42px" }}
                                >
                                  {prod.product_image ? (
                                    <img
                                      src={prod.product_image}
                                      alt={prod.product_name}
                                      style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                    />
                                  ) : (
                                    <i className="bi bi-box-seam text-secondary opacity-50"></i>
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="fw-bold text-dark">{prod.product_name}</div>
                                {inCartQty > 0 && (
                                  <span className="badge bg-success" style={{ fontSize: "10px" }}>
                                    <i className="bi bi-cart-check me-1"></i>{inCartQty} in cart
                                  </span>
                                )}
                              </td>
                              <td>
                                <span className="badge bg-light text-muted border">
                                  {prod.product_weight || "-"}
                                </span>
                              </td>
                              <td className="text-end">
                                {hasDiscount && (
                                  <span className="text-muted text-decoration-line-through small me-2">₹{mrp}</span>
                                )}
                                <span className="fw-bold text-success fs-6">₹{sellingPrice}</span>
                              </td>
                              <td className="text-center">
                                {inCartQty > 0 ? (
                                  <div
                                    className="d-inline-flex align-items-center bg-light rounded-pill border px-1 shadow-sm"
                                    style={{ height: "28px" }}
                                  >
                                    <button
                                      type="button"
                                      className="btn btn-sm text-secondary p-0 d-flex align-items-center justify-content-center"
                                      style={{
                                        width: "22px",
                                        height: "22px",
                                        borderRadius: "50%",
                                        border: "none",
                                        background: "transparent",
                                      }}
                                      onClick={() => updateQty(prod.product_id, -1)}
                                      title="Decrease quantity"
                                    >
                                      <i className="bi bi-dash fs-6"></i>
                                    </button>

                                    <span
                                      className="text-dark fw-bold text-center user-select-none"
                                      style={{ minWidth: "24px", fontSize: "12px" }}
                                    >
                                      {inCartQty}
                                    </span>

                                    <button
                                      type="button"
                                      className="btn btn-sm text-success p-0 d-flex align-items-center justify-content-center"
                                      style={{
                                        width: "22px",
                                        height: "22px",
                                        borderRadius: "50%",
                                        border: "none",
                                        background: "transparent",
                                      }}
                                      onClick={() => addToCart(prod)}
                                      title="Increase quantity"
                                    >
                                      <i className="bi bi-plus fs-6"></i>
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    className="btn btn-sm btn-outline-success rounded-pill px-3 fw-bold"
                                    onClick={() => addToCart(prod)}
                                  >
                                    <i className="bi bi-plus-lg me-1"></i> Add
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              ) : (
                <div className="text-center py-5">
                  <i className="bi bi-search text-muted opacity-50 fs-1 d-block mb-2"></i>
                  <h6 className="text-dark fw-bold">No matching products found</h6>
                  <p className="text-muted small">Try searching with a different keyword or category.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Current Order / Cart Panel */}
        <div className="col-lg-4 col-xl-4">
          <div className="card shadow-sm border-0 d-flex flex-column" style={{ height: "calc(100vh - 145px)", minHeight: "600px", borderRadius: "12px" }}>
            {/* Cart Header */}
            <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <h5 className="mb-0 fw-bold text-dark">Current Order</h5>
                {totalCartItemCount > 0 && (
                  <span className="badge bg-success rounded-pill px-2">{totalCartItemCount}</span>
                )}
              </div>

              <div className="d-flex align-items-center gap-1">
                {/* Global Discount Trigger with metadata */}
                <button
                  className={`btn btn-sm fw-bold d-flex align-items-center ${
                    globalDiscountValue > 0 ? "btn-success text-white shadow-sm" : "btn-outline-success"
                  }`}
                  onClick={() => openDiscountModal(null)}
                  title={`Discount: ${globalDiscountValue}% | Base: ${globalDiscountBase === "mrp" ? "MRP" : "Selling Price"} | Mode: ${isGlobalDiscountManual ? "Manual" : "Auto Qty"}`}
                  style={{ fontSize: "12px" }}
                >
                  <i className="bi bi-tag-fill me-1"></i>
                  {globalDiscountValue > 0 ? (
                    <>
                      <span>{globalDiscountType === "percent" ? `${globalDiscountValue}%` : `₹${globalDiscountValue}`}</span>
                      <span className="badge bg-white text-dark ms-1 px-1 py-0 fw-bold" style={{ fontSize: "10px" }}>
                        {globalDiscountBase === "mrp" ? "MRP" : "SP"}
                      </span>
                      <span className="badge bg-black bg-opacity-25 text-white ms-1 px-1 py-0" style={{ fontSize: "10px" }}>
                        {isGlobalDiscountManual ? "Manual" : "Auto"}
                      </span>
                    </>
                  ) : (
                    <span>Discount</span>
                  )}
                </button>

                {/* Clear Cart */}
                {cart.length > 0 && (
                  <button className="btn btn-sm btn-outline-danger" onClick={clearCart} title="Clear Cart">
                    <i className="bi bi-trash"></i>
                  </button>
                )}
              </div>
            </div>

            <div className="card-body d-flex flex-column p-0 flex-grow-1 overflow-hidden">
              {/* Customer Selector Card */}
              <div className="p-3 bg-light border-bottom position-relative" style={{ zIndex: 1050 }}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="form-label fw-bold small text-muted text-uppercase mb-0">
                    Customer {selectedCustomer ? "" : "(Default: Walk-in)"}
                  </label>
                  {!selectedCustomer && (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none small text-success fw-bold"
                      onClick={handleSelectWalkIn}
                    >
                      + Quick Walk-in
                    </button>
                  )}
                </div>

                {selectedCustomer ? (
                  <div className="d-flex justify-content-between align-items-center bg-white p-2 px-3 rounded-3 border shadow-sm">
                    <div className="d-flex align-items-center gap-2">
                      <div className="rounded-circle bg-success bg-opacity-10 text-success p-2 d-flex align-items-center justify-content-center" style={{ width: "36px", height: "36px" }}>
                        <i className="bi bi-person-fill fs-5"></i>
                      </div>
                      <div>
                        <div className="fw-bold text-dark small">{selectedCustomer.customer_name}</div>
                        <div className="text-muted" style={{ fontSize: "11px" }}>
                          {selectedCustomer.mobile_no ? `Phone: ${selectedCustomer.mobile_no}` : "Walk-in Customer"}
                        </div>
                      </div>
                    </div>
                    
                    {/* Badge and Close Button Container */}
                    <div className="d-flex align-items-center gap-2">
                      {selectedCustomer.mobile_no && renderBrandCustomerTag(customerBrandType, customerOrderHistory.length, () => setShowHistoryModal(true))}
                      <button
                        className="btn btn-sm btn-outline-secondary rounded-circle"
                        style={{ width: "28px", height: "28px", padding: 0 }}
                        onClick={() => { setSelectedCustomer(null); resetGiftEligibility(); }}
                        title="Change Customer"
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="position-relative w-100">
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-white border-end-0 text-muted">
                        <i className="bi bi-person-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control border-start-0 ps-0"
                        placeholder="Search name or 10-digit mobile..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                      />
                    </div>

                    {customerSearch && (
                      <ul
                        className="list-group position-absolute w-100 shadow-lg border bg-white"
                        style={{ 
                          zIndex: 1060, 
                          maxHeight: "220px", 
                          overflowY: "auto",
                          top: "calc(100% + 4px)", // 👈 Forces the dropdown strictly below the input field
                          left: 0,
                          margin: 0
                        }}
                      >
                        {customers.slice(0, 8).map((c) => (
                          <li
                            className="list-group-item list-group-item-action cursorP py-2 small d-flex justify-content-between align-items-center"
                            key={c.customer_id}
                            onClick={() => {
                              setSelectedCustomer(c);
                              setCustomerSearch("");
                              resetGiftEligibility();
                            }}
                          >
                            <div>
                              <strong className="text-dark">{c.customer_name}</strong>
                              <div className="text-muted" style={{ fontSize: "11px" }}>{c.mobile_no}</div>
                            </div>
                            <span className="badge bg-light text-primary border">Select</span>
                          </li>
                        ))}
                        <li
                          className="list-group-item list-group-item-action text-success fw-bold cursorP py-2 small"
                          style={{ backgroundColor: "#f0fdf4" }}
                          onClick={() => {
                            setNewCustomerMobile(customerSearch);
                            setShowAddCustomer(true);
                            setCustomerSearch("");
                          }}
                        >
                          <i className="bi bi-person-plus-fill me-1"></i> Add "{customerSearch}" as New Customer
                        </li>
                      </ul>
                    )}
                  </div>
                )}

              </div>



              {/* Cart Items List */}
              <div className="flex-grow-1 overflow-auto p-3 position-relative" style={{ zIndex: 1 }}>
                {cart.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-bag text-secondary opacity-25" style={{ fontSize: "3.5rem" }}></i>
                    <h6 className="mt-3 text-dark fw-bold">Cart is empty</h6>
                    <p className="small text-muted mb-0">Click products on the left to start billing.</p>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {cart.map((item) => {
                      const itemDisc = getItemCalculatedDiscount(item);
                      const itemBase = globalDiscountBase === "mrp" ? item.totalMrp : item.totalSellingPrice;
                      const effectiveBase = item.hasCustomDiscount
                        ? item.discountBase === "mrp"
                          ? item.totalMrp
                          : item.totalSellingPrice
                        : itemBase;
                      const finalTotal = effectiveBase - itemDisc;

                      return (
                        <div
                          key={item.is_free_item ? 'free_'+item.product_id : item.product_id}
                          className="p-2 px-3 bg-white rounded-3 border shadow-sm"
                        >
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <div style={{ maxWidth: "65%" }}>
                              <div className="fw-bold text-dark text-truncate" style={{ fontSize: "13px" }}>
                                {item.product_name}
                              </div>
                              <div className="text-muted" style={{ fontSize: "11px" }}>
                                {item.is_free_item ? (
                                  <>
                                    <span className="badge bg-success me-1" style={{ fontSize: "9px" }}>FREE ITEM</span>
                                    <span>× {item.qty}</span>
                                  </>
                                ) : globalDiscountBase === "mrp" ? (
                                  item.sellingPrice < item.mrp ? (
                                    <>
                                      <span>₹{item.mrp}</span>
                                      <span className="text-decoration-line-through mx-1 text-muted">₹{item.sellingPrice}</span>
                                      <span>× {item.qty}</span>
                                    </>
                                  ) : (
                                    <span>₹{item.mrp} × {item.qty}</span>
                                  )
                                ) : item.sellingPrice < item.mrp ? (
                                  <>
                                    <span className="text-decoration-line-through me-1 text-muted">₹{item.mrp}</span>
                                    <span>₹{item.sellingPrice} × {item.qty}</span>
                                  </>
                                ) : (
                                  <span>₹{item.sellingPrice} × {item.qty}</span>
                                )}
                                {item.hasCustomDiscount && !item.is_free_item && (
                                  <span className="badge bg-warning text-dark ms-1" style={{ fontSize: "9px" }}>
                                    Override
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="text-end">
                              <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                                ₹{finalTotal.toFixed(2)}
                              </div>
                              {itemDisc > 0 && (
                                <div className="text-success" style={{ fontSize: "10px", fontWeight: "600" }}>
                                  -₹{itemDisc.toFixed(2)} {getDiscountDetails(item)}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                            {item.is_free_item ? (
                              <div className="text-success small" style={{ fontSize: "11px", fontWeight: "600" }}>
                                <i className="bi bi-gift-fill me-1"></i> Auto-Applied Offer
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-link p-0 text-decoration-none text-muted small"
                                onClick={() => openDiscountModal(item)}
                                style={{ fontSize: "11px" }}
                              >
                                <i className="bi bi-tag-fill text-success me-1"></i> Edit Item Disc
                              </button>
                            )}

                            <div className="d-flex align-items-center gap-2">
                              {/* 🟢 Modern Rounded Pill Stepper */}
                              <div
                                className={`d-flex align-items-center rounded-pill border px-1 shadow-sm ${item.is_free_item ? 'bg-secondary bg-opacity-10 border-secondary border-opacity-25' : 'bg-light'}`}
                                style={{ height: "30px", cursor: item.is_free_item ? "not-allowed" : "default" }}
                              >
                                <button
                                  type="button"
                                  className={`btn btn-sm p-0 d-flex align-items-center justify-content-center ${item.is_free_item ? 'text-secondary opacity-25' : 'text-secondary'}`}
                                  style={{
                                    width: "24px",
                                    height: "24px",
                                    borderRadius: "50%",
                                    border: "none",
                                    background: "transparent",
                                  }}
                                  onClick={() => updateQty(item.product_id, -1)}
                                  title="Decrease quantity"
                                  disabled={item.is_free_item}
                                >
                                  <i className="bi bi-dash fs-6"></i>
                                </button>

                                <span
                                  className={`fw-bold text-center user-select-none ${item.is_free_item ? 'text-secondary' : 'text-dark'}`}
                                  style={{ minWidth: "26px", fontSize: "13px" }}
                                >
                                  {item.qty}
                                </span>

                                <button
                                  type="button"
                                  className={`btn btn-sm p-0 d-flex align-items-center justify-content-center ${item.is_free_item ? 'text-secondary opacity-25' : 'text-success'}`}
                                  style={{
                                    width: "24px",
                                    height: "24px",
                                    borderRadius: "50%",
                                    border: "none",
                                    background: "transparent",
                                  }}
                                  onClick={() => updateQty(item.product_id, 1)}
                                  title="Increase quantity"
                                  disabled={item.is_free_item}
                                >
                                  <i className="bi bi-plus fs-6"></i>
                                </button>
                              </div>

                              {/* 🟢 Clean Circular Trash Icon */}
                              {!item.is_free_item && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-light text-danger rounded-circle p-0 d-flex align-items-center justify-content-center border shadow-sm"
                                  style={{ width: "30px", height: "30px" }}
                                  onClick={() => removeFromCart(item.product_id)}
                                  title="Remove item"
                                >
                                  <i className="bi bi-trash3" style={{ fontSize: "13px" }}></i>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Bill & Checkout Footer */}
              <div className="border-top p-3 bg-light mt-auto">
                {/* Payment Mode Selector */}
                <div className="mb-3">
                  <label className="form-label fw-bold small text-muted text-uppercase mb-1">Payment Method</label>
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${paymentType === "cash" ? "btn-success text-white fw-bold shadow-sm" : "btn-outline-secondary"}`}
                      onClick={() => setPaymentType("cash")}
                    >
                      <i className="bi bi-cash me-1"></i> Cash
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${paymentType === "online" ? "btn-success text-white fw-bold shadow-sm" : "btn-outline-secondary"}`}
                      onClick={() => setPaymentType("online")}
                    >
                      <i className="bi bi-qr-code me-1"></i> Online / UPI
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${paymentType === "credit" ? "btn-warning text-dark fw-bold shadow-sm" : "btn-outline-secondary"}`}
                      onClick={() => setPaymentType("credit")}
                    >
                      <i className="bi bi-hourglass-split me-1"></i> Credit
                    </button>
                  </div>
                </div>

                {/* Subtotals */}
                <div className="d-flex justify-content-between small text-muted mb-1">
                  <span>Sub Total</span>
                  <span className="fw-semibold text-dark">₹{cartSubtotal.toFixed(2)}</span>
                </div>

                {cartTotalDiscount > 0 && (
                  <div className="d-flex justify-content-between small text-success mb-2 fw-semibold">
                    <span>Discount {isGlobalDiscountManual ? "(Manual)" : "(Auto Qty)"}</span>
                    <span>-₹{cartTotalDiscount.toFixed(2)}</span>
                  </div>
                )}

                <div className="d-flex justify-content-between align-items-center fs-5 fw-bold mb-3 border-top pt-2">
                  <span className="text-dark">Grand Total</span>
                  <span className="text-success fs-4">₹{cartFinalTotal.toFixed(2)}</span>
                </div>

                {/* Information hint when no specific customer is selected */}
                {cart.length > 0 && !selectedCustomer && (
                  <div className="d-flex align-items-center justify-content-between bg-white border border-success-subtle rounded-3 p-2 mb-3 text-success small">
                    <span className="d-flex align-items-center gap-1">
                      <i className="bi bi-person-check-fill"></i>
                      <span>Billing as: <strong>Walk-in Customer</strong></span>
                    </span>
                    <span className="badge bg-success-subtle text-success border border-success-subtle">Default</span>
                  </div>
                )}

                {/* Submit Checkout Button (Always active if cart has items) */}
                <button
                  className="btn btn-success w-100 py-2 fw-bold fs-6 shadow d-flex align-items-center justify-content-center gap-2"
                  style={{ borderRadius: "10px" }}
                  disabled={cart.length === 0 || isSubmitting}
                  onClick={handleInitiateBookOrder}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      <span>Booking Order...</span>
                    </>
                  ) : cart.length === 0 ? (
                    <>
                      <i className="bi bi-cart-x fs-5"></i>
                      <span>Add Products to Order</span>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-receipt fs-5"></i>
                      <span>Book Order (₹{cartFinalTotal.toFixed(2)})</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Discount Configuration Modal */}
      {showDiscountModal && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(15, 23, 42, 0.6)", zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "14px", overflow: "hidden" }}>
              <div className="modal-header bg-success text-white py-3 px-4">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-tag-fill me-2"></i>
                  {editingDiscountItem ? `Item Discount: ${editingDiscountItem.product_name}` : "Global Cart Discount"}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowDiscountModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                {!editingDiscountItem && (
                  <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded-3 border">
                    <div>
                      <div className="fw-bold text-dark">Automatic Qty Discount</div>
                      <div className="small text-muted">
                        {isGlobalDiscountManual ? "Manual Override Active" : "Auto Calculating Based on Items in Cart"}
                      </div>
                    </div>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input fs-4"
                        type="checkbox"
                        checked={!isGlobalDiscountManual}
                        onChange={toggleAutoDiscount}
                      />
                    </div>
                  </div>
                )}

                {/* Discount Value & Type (Grayed out only if Auto Qty Discount is active) */}
                <div style={{ opacity: !editingDiscountItem && !isGlobalDiscountManual ? 0.6 : 1, pointerEvents: !editingDiscountItem && !isGlobalDiscountManual ? "none" : "auto" }}>
                  <div className="mb-3">
                    <label className="form-label fw-bold text-muted small text-uppercase">Discount Value</label>
                    <div className="input-group input-group-lg">
                      {tempDiscountType === "flat" && <span className="input-group-text bg-light fw-bold">₹</span>}
                      <input
                        type="number"
                        className="form-control fw-bold"
                        value={!editingDiscountItem && !isGlobalDiscountManual ? globalDiscountValue : tempDiscountValue}
                        onChange={(e) => setTempDiscountValue(e.target.value)}
                        placeholder="0"
                      />
                      {tempDiscountType === "percent" && <span className="input-group-text bg-light fw-bold">%</span>}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold text-muted small text-uppercase">Discount Type</label>
                    <div className="btn-group w-100">
                      <button
                        type="button"
                        className={`btn ${tempDiscountType === "percent" ? "btn-success fw-bold text-white shadow-sm" : "btn-outline-secondary"}`}
                        onClick={() => setTempDiscountType("percent")}
                      >
                        Percentage (%)
                      </button>
                      <button
                        type="button"
                        className={`btn ${tempDiscountType === "flat" ? "btn-success fw-bold text-white shadow-sm" : "btn-outline-secondary"}`}
                        onClick={() => setTempDiscountType("flat")}
                      >
                        Flat Amount (₹)
                      </button>
                    </div>
                  </div>
                </div>

                {/* 🟢 Calculate From Base: ALWAYS ENABLED (Never disabled in Auto or Manual) */}
                <div className="mb-2 pt-2 border-top">
                  <label className="form-label fw-bold text-dark small text-uppercase mb-2">
                    Calculate From Base
                  </label>
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm py-2 ${tempDiscountBase === "sellingPrice" ? "btn-success fw-bold text-white shadow-sm" : "btn-outline-secondary bg-white"}`}
                      onClick={() => setTempDiscountBase("sellingPrice")}
                    >
                      Selling Price
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm py-2 ${tempDiscountBase === "mrp" ? "btn-success fw-bold text-white shadow-sm" : "btn-outline-secondary bg-white"}`}
                      onClick={() => setTempDiscountBase("mrp")}
                    >
                      MRP
                    </button>
                  </div>
                  <div className="form-text text-muted mt-1" style={{ fontSize: "11px" }}>
                    Choose whether percentage discounts apply on the MRP or on the Selling Price.
                  </div>
                </div>
              </div>

              <div className="modal-footer bg-light px-4 py-3 d-flex justify-content-between">
                <button type="button" className="btn btn-outline-danger" onClick={removeDiscountModal}>
                  Remove Discount
                </button>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowDiscountModal(false)}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-success fw-bold px-4" onClick={applyDiscountModal}>
                    Apply Discount
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tax Invoice Bill Modal */}
      {renderTaxInvoiceModal()}

      {/* Add New Customer Modal */}
      {showAddCustomer && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(15, 23, 42, 0.6)", zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "14px", overflow: "hidden" }}>
              <div className="modal-header bg-success text-white py-3 px-4">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-person-plus-fill me-2"></i> Add Walk-in Customer
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowAddCustomer(false)}></button>
              </div>
              <form onSubmit={handleAddCustomer}>
                <div className="modal-body p-4">
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="form-label fw-bold text-dark small text-uppercase mb-0">
                        Mobile Number <span className="text-danger">*</span>
                      </label>
                      {newCustomerMobile.length >= 10 && renderBrandCustomerTag(modalBrandType, customerOrderHistory.length, () => setShowHistoryModal(true))}
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="10-digit mobile number"
                      value={newCustomerMobile}
                      onChange={(e) => setNewCustomerMobile(e.target.value)}
                      required
                      autoFocus
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold text-muted">Customer Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Customer full name"
                      value={newCustomerName}
                      onChange={(e) => setNewCustomerName(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer bg-light px-4 py-3">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAddCustomer(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-success fw-bold px-4" disabled={isAddingCustomer}>
                    {isAddingCustomer ? "Adding..." : "Add & Select"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* 🟢 Optional Customer Mobile Prompt Modal on Checkout */}
      {showOptionalPhoneModal && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(15, 23, 42, 0.65)", zIndex: 1055 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "14px", overflow: "hidden" }}>
              <div className="modal-header bg-success text-white py-3 px-4">
                <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                  <i className="bi bi-telephone-plus"></i> Customer Details (Optional)
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowOptionalPhoneModal(false)}></button>
              </div>

              <form onSubmit={handleSavePhoneAndBook}>
                <div className="modal-body p-4">
                  <div className="alert alert-light border d-flex align-items-center gap-2 mb-3 py-2 px-3 small text-muted">
                    <i className="bi bi-whatsapp text-success fs-5"></i>
                    <span>Entering customer mobile number helps in sending the <strong>WhatsApp Invoice</strong>. You can also skip this.</span>
                  </div>

                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="form-label fw-bold text-dark small text-uppercase mb-0">
                        Mobile Number <span className="text-muted fw-normal">(Optional)</span>
                      </label>
                      {optionalMobile.length >= 10 && renderBrandCustomerTag(modalBrandType, customerOrderHistory.length, () => setShowHistoryModal(true))}
                    </div>
                    <div className="input-group">
                      <span className="input-group-text bg-light fw-bold text-muted">+91</span>
                      <input
                        type="tel"
                        className="form-control form-control-lg fw-bold"
                        placeholder="10-digit mobile number"
                        value={optionalMobile}
                        onChange={(e) => setOptionalMobile(e.target.value)}
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="mb-2">
                    <label className="form-label fw-bold text-dark small text-uppercase">
                      Customer Name <span className="text-muted fw-normal">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Ramesh Patel (Optional)"
                      value={optionalName}
                      onChange={(e) => setOptionalName(e.target.value)}
                    />
                  </div>
                </div>

                <div className="modal-footer bg-light px-4 py-3 d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-3"
                    onClick={handleSkipPhoneAndBook}
                    disabled={isSubmitting}
                  >
                    <i className="bi bi-arrow-right-short me-1"></i> Skip & Bill as Walk-in
                  </button>

                  <button
                    type="submit"
                    className="btn btn-success fw-bold px-4 shadow-sm"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1"></span> Booking...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check2-circle me-1"></i> Save & Book Order
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Order History Modal */}
      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="lg" centered style={{ zIndex: 1060 }}>
        <div className="modal-header bg-light">
          <h5 className="modal-title fw-bold text-dark">
            <i className="bi bi-clock-history me-2 text-primary"></i>
            Order History {selectedCustomer?.customer_name ? `- ${selectedCustomer.customer_name}` : ''}
          </h5>
          <button type="button" className="btn-close" onClick={() => setShowHistoryModal(false)}></button>
        </div>
        <div className="modal-body p-0">
          <div className="table-responsive" style={{maxHeight: '60vh', overflowY: 'auto'}}>
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light sticky-top">
                <tr>
                  <th className="px-3">Date</th>
                  <th>Order Code</th>
                  <th>Branch</th>
                  <th>Products</th>
                  <th className="text-end px-3">Amount</th>
                </tr>
              </thead>
              <tbody>
                {customerOrderHistory.length > 0 ? (
                  customerOrderHistory.map((order, idx) => (
                    <tr key={idx}>
                      <td className="px-3"><span className="text-muted small fw-semibold">{order.orderAt ? new Date(order.orderAt).toLocaleDateString('en-GB') : '-'}</span></td>
                      <td>
                        <span className="fw-bold text-primary small">{order.orderCode || '-'}</span>
                      </td>
                      <td><span className="badge bg-light text-dark border">{order.branchName || "Unknown"}</span></td>
                      <td style={{ maxWidth: '250px' }}>
                        <div className="text-truncate small" title={order.products}>
                          {order.products ? order.products : <span className="text-muted fst-italic">No details</span>}
                        </div>
                      </td>
                      <td className="text-end fw-bold text-success px-3">₹{order.totalAmount}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-5 text-muted">No order history found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
    </main>
  );
}

export default POS;
